import { useDispatch, useSelector } from "react-redux";
import { ConfigProvider, Empty, Layout, notification, Spin } from "antd";
import UMCSidebar from "./components/Layout/UMCSidebar";
import UMCHeader from "./components/Layout/UMCHeader";
import UMCFooter from "./components/Layout/UMCFooter";
import UMCContent from "./components/Layout/UMCContent";
import UMCSubHeader from "./components/Layout/UMCSubHeader";
import {
  DEFAULT_BTN,
  DEFAULT_RADIO_BTN,
  DEFAULT_SELECT,
  DEFAULT_TABLE,
  DEFAULT_DIVIDER,
  DEFAULT_STEPS,
  GREY,
  PURPLE,
  getBaseFontSize,
  DEFAULT_MODAL_STYLE,
  DEFAULT_PAGINATION,
  DEFAULT_NOTIFICATION,
} from "./styles/styleUtils";
import { useWindowSize } from "./components/customHooks/useWindowSize";
import { useEffect, useState, useRef } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import {
  fetchAndConcatenateDictionaries,
  fetchOxeErrorCodesByLang,
} from "./store/actions/CommonActions";
import {
  clearSession,
  isTokenExpired,
  setRefreshTimeout,
} from "./utils/SecurityUtils";
import { useNavigate } from "react-router-dom";
import { setTimerArr } from "./store/reducers/CommonReducer";
import {
  getOxeState,
  oxeLoginExpireHandler,
  refreshCacheOXEModal,
} from "./store/actions/OxeActions";
import { TOKEN_1, TOKEN_2, INACTIVE_TIME } from "./utils/Constants";
import { getSessionItem } from "./utils/storageUtils";
import { jwtDecode } from "jwt-decode";
import { handleOXEDisconnection } from "../src/store/actions/OxeActions";
import { getTranslated, setNavigator } from "./utils/CommonUtils";
import { useOneTrust } from "./components/customHooks/useOneTrust";
import { initializeGTM, removeGTM } from "./utils/GTMUtils";


function App() {
  const loading = useSelector((state) => state.common.loading);
  const language = useSelector((state) => state.common.language);
  const timerArr = useSelector((state) => state.common.timerArr);
  const umcNotification = useSelector((state) => state.common.umcNotification);
  const oxeAuthStatus = useSelector((state) => state.oxe.oxeAuthStatus);
  const dict = useSelector((state) => state.common.dict);
  const oxeClientInfo = useSelector((state) => state.oxe.oxeAndClientInfo);

  const [width] = useWindowSize();
  const [fontSize, setFontSize] = useState();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [api, contextHolder] = notification.useNotification();

  //const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // OneTrust Consent Hook
  const { hasAnalyticsConsent } = useOneTrust();

  useEffect(() => {
    window.addEventListener("storage", (event) => {
      // Sync storage details across tabs
      if ((event.key === null || event.key === "sIndex") && !event.newValue) {
        // timerArr?.length && clearTimeouts(timerArr)
        dispatch(setTimerArr([]));

        navigate("/logout");
      }
    });

    handleToken1Refresh();
    handleToken2Refresh();
    getSessionItem(TOKEN_2) && refreshCacheOXEModal(); // Sync OXE data in UMC

    return () => {
      window.removeEventListener("storage", (event) => {
        console.log("Storage listener removed");
      });
    };
  }, []);

  useEffect(() => {
    setNavigator(navigate);
  }, [navigate]);

  useEffect(() => {
    if (language) {
      fetchAndConcatenateDictionaries(language, dispatch);
    }
  }, [language]);

  // Google Analytics Initialization based on Consent
  useEffect(() => {
    if (hasAnalyticsConsent) {
      initializeGTM()
    } else {
      removeGTM()
    }
  }, [hasAnalyticsConsent]); // Re-run when hasConsent changes

  useEffect(() => {
    if (oxeAuthStatus)
      fetchOxeErrorCodesByLang(language, oxeClientInfo, dispatch);
  }, [oxeAuthStatus, language]);

  useEffect(() => {
    setFontSize(getBaseFontSize(width));
  }, [width]);

  const handleToken1Refresh = () => {
    const token1 = getSessionItem(TOKEN_1);

    // if((token1 && !isTokenExpired(token1)) || !token1) { //Check
    dispatch(
      getOxeState(handleToken1Refresh, (status) => {
        if (!status) {
          navigate("/logout");
        }
      }),
    );
    // }
  };

  const handleToken2Refresh = () => {
    const token2 = getSessionItem(TOKEN_2);

    if (token2) {
      if (!isTokenExpired(token2)) {
        const decoded = jwtDecode(token2);
        let timer = setRefreshTimeout(decoded.exp, () =>
          oxeLoginExpireHandler(dispatch),
        );
      } else {
        oxeLoginExpireHandler(dispatch);
      }
    }
  };

  useEffect(() => {
    umcNotification.type && openUMCNotification();
  }, [umcNotification]);

  useEffect(() => {
    if (oxeAuthStatus) {
      resetTimer();

      window.addEventListener("mousemove", resetTimer);
      window.addEventListener("keydown", resetTimer);
      window.addEventListener("mousedown", resetTimer);
      window.addEventListener("scroll", resetTimer);

      return () => {
        clearTimeout(timerRef.current);
        window.removeEventListener("mousemove", resetTimer);
        window.removeEventListener("keydown", resetTimer);
        window.removeEventListener("mousedown", resetTimer);
        window.removeEventListener("scroll", resetTimer);
      };
    }
  }, [oxeAuthStatus]);

  const resetTimer = () => {
    lastActivityRef.current = Date.now();
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(checkInactivity, INACTIVE_TIME * 1000);
  };

  const checkInactivity = () => {
    const now = Date.now();
    const elapsed = (now - lastActivityRef.current) / 1000;

    if (elapsed >= INACTIVE_TIME) {
      handleInactivityLogout();
    } else {
      timerRef.current = setTimeout(checkInactivity, INACTIVE_TIME - elapsed);
    }
  };

  const handleInactivityLogout = () => {
    console.log("Logout due to inactivity..");
    dispatch(handleOXEDisconnection());
    window.location.reload();
  };

  const openUMCNotification = () => {
    api[umcNotification.type]({
      message: umcNotification.message,
      description: umcNotification.description,
      placement: "bottomRight",
    });
  };

  const customizeRenderEmpty = () => {
    return (
      <Empty
        description={getTranslated(dict, "MSG_NO_DATA")}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  };

  return (
    <ConfigProvider
      theme={{
        // To override antd's default style globally
        token: {
          fontSize: fontSize,
          fontFamily: "Noto Sans",
          colorPrimary: PURPLE,
        },
        components: {
          Button: DEFAULT_BTN,
          Radio: DEFAULT_RADIO_BTN,
          Select: DEFAULT_SELECT,
          Table: DEFAULT_TABLE,
          Divider: DEFAULT_DIVIDER,
          Steps: DEFAULT_STEPS,
          Pagination: DEFAULT_PAGINATION,
          Modal: DEFAULT_MODAL_STYLE,
          Notification: DEFAULT_NOTIFICATION
        },
      }}
      renderEmpty={customizeRenderEmpty}
    >
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined />}
        size="large"
        wrapperClassName={loading ? "spin-active" : "spin-idle"}
      >
        <Layout>
          <Layout>
            <UMCSidebar />
            <Layout
              style={{
                backgroundColor: GREY,
                minWidth: "500px",
                overflow: "auto",
              }}
            >
              <UMCHeader status={200} unAuth={false} />
              <UMCSubHeader />
              <UMCContent />
            </Layout>
          </Layout>
          <UMCFooter />
        </Layout>
        {contextHolder}
      </Spin>
    </ConfigProvider>
  );
}

export default App;
